/**
 * RBAC V2 — Constantes des permissions
 * =====================================
 *
 * Source de vérité : `/app/backend/models/rbac.py` (DEFAULT_PERMISSIONS).
 * Centralise les chaînes pour éviter les typos et offrir l'autocomplete IDE.
 *
 * Usage :
 *   import { PERMS } from '@/utils/permissions';
 *   const { hasPermission } = usePermissions();
 *   if (hasPermission(PERMS.OPERATIONS_VIEW_PRICING)) { ... }
 *
 *   <Can permission={PERMS.FINANCE_PAYMENTS_REFUND}><Button .../></Can>
 *
 * Quand on ajoute une perm en backend, l'ajouter aussi ici (alphabétique par module).
 */

export const PERMS = Object.freeze({
  // ===== OPERATIONS =====
  OPERATIONS_VIEW_OWN: 'operations.view.own',
  OPERATIONS_VIEW_ASSIGNED: 'operations.view.assigned',
  OPERATIONS_VIEW_ALL: 'operations.view.all',
  OPERATIONS_CREATE: 'operations.create',
  OPERATIONS_EDIT_OWN: 'operations.edit.own',
  OPERATIONS_EDIT_ALL: 'operations.edit.all',
  OPERATIONS_DELETE: 'operations.delete',
  OPERATIONS_ASSIGN: 'operations.assign',
  OPERATIONS_CHANGE_STATUS: 'operations.change_status',
  OPERATIONS_PLAN: 'operations.plan',
  OPERATIONS_BULK_IMPORT: 'operations.bulk_import',
  OPERATIONS_BULK_ACTIONS: 'operations.bulk_actions',
  OPERATIONS_MULTI_STOP: 'operations.multi_stop',
  OPERATIONS_CHILDREN_MANAGE: 'operations.children.manage',
  OPERATIONS_EXPORT: 'operations.export',
  OPERATIONS_VIEW_PRICING: 'operations.view_pricing',

  // ===== TRANSFER MODIFICATIONS =====
  TRANSFER_MODIFICATIONS_VIEW_ALL: 'transfer_modifications.view.all',
  TRANSFER_MODIFICATIONS_VIEW_OWN: 'transfer_modifications.view.own',
  TRANSFER_MODIFICATIONS_CREATE: 'transfer_modifications.create',
  TRANSFER_MODIFICATIONS_APPROVE: 'transfer_modifications.approve',
  TRANSFER_MODIFICATIONS_REJECT: 'transfer_modifications.reject',
  TRANSFER_MODIFICATIONS_DELETE: 'transfer_modifications.delete',

  // ===== QUOTES =====
  QUOTES_VIEW_OWN: 'quotes.view.own',
  QUOTES_VIEW_ALL: 'quotes.view.all',
  QUOTES_CREATE: 'quotes.create',
  QUOTES_EDIT: 'quotes.edit',
  QUOTES_APPROVE: 'quotes.approve',
  QUOTES_REJECT: 'quotes.reject',
  QUOTES_NEGOTIATE: 'quotes.negotiate',

  // ===== CONTRACTS =====
  CONTRACTS_VIEW_ALL: 'contracts.view.all',
  CONTRACTS_CREATE: 'contracts.create',
  CONTRACTS_EDIT: 'contracts.edit',
  CONTRACTS_DELETE: 'contracts.delete',
  CONTRACTS_SUSPEND: 'contracts.suspend',
  CONTRACTS_INVOICE: 'contracts.invoice',
  CONTRACTS_EXPORT: 'contracts.export',
  CONTRACTS_VIEW_PRICING: 'contracts.view_pricing',
  CONTRACTS_ANALYTICS: 'contracts.analytics',
  CONTRACTS_ATTENDANCE: 'contracts.attendance',
  CONTRACTS_HOLIDAYS: 'contracts.holidays',
  CONTRACT_QUOTES_VIEW: 'contract_quotes.view',
  CONTRACT_QUOTES_CREATE: 'contract_quotes.create',
  CONTRACT_QUOTES_APPROVE: 'contract_quotes.approve',

  // ===== LOADING NOTES =====
  LOADING_NOTES_VIEW_ALL: 'loading_notes.view.all',
  LOADING_NOTES_VIEW_ASSIGNED: 'loading_notes.view.assigned',
  LOADING_NOTES_CREATE: 'loading_notes.create',
  LOADING_NOTES_EDIT: 'loading_notes.edit',
  LOADING_NOTES_DELETE: 'loading_notes.delete',
  LOADING_NOTES_SIGN: 'loading_notes.sign',
  LOADING_NOTES_VALIDATE: 'loading_notes.validate',
  LOADING_NOTES_UPLOAD_PHOTOS: 'loading_notes.upload_photos',
  LOADING_NOTES_EXPORT_ZIP: 'loading_notes.export_zip',

  // ===== USERS =====
  USERS_VIEW_ALL: 'users.view.all',
  USERS_CREATE: 'users.create',
  USERS_EDIT: 'users.edit',
  USERS_DELETE: 'users.delete',
  USERS_CHANGE_ROLE: 'users.change_role',
  AUTH_REVOKE_SESSIONS: 'auth.revoke_sessions',
  AUTH_ACCESS_REQUESTS_APPROVE: 'auth.access_requests.approve',
  AUTH_OAUTH_CONFIGURE: 'auth.oauth.configure',

  // ===== MESSAGING =====
  MESSAGING_VIEW_OWN: 'messaging.view.own',
  MESSAGING_VIEW_ALL: 'messaging.view.all',
  MESSAGING_SEND: 'messaging.send',
  MESSAGING_DELETE: 'messaging.delete',
  MESSAGING_MANAGE_TEMPLATES: 'messaging.manage_templates',

  // ===== FINANCES (legacy) =====
  FINANCES_VIEW_OWN: 'finances.view.own',
  FINANCES_VIEW_ALL: 'finances.view.all',
  FINANCES_CREATE_INVOICE: 'finances.create_invoice',
  FINANCES_MARK_PAID: 'finances.mark_paid',
  FINANCES_EXPORT: 'finances.export',

  // ===== FINANCE (étendu V2) =====
  FINANCE_COCKPIT_VIEW: 'finance.cockpit.view',
  FINANCE_COCKPIT_FULL: 'finance.cockpit.full',
  FINANCE_AUDIT_VIEW: 'finance.audit.view',
  FINANCE_AUDIT_REGENERATE: 'finance.audit.regenerate',
  FINANCE_AUDIT_BACKFILL: 'finance.audit.backfill',
  FINANCE_INVOICES_CREATE_CREDIT_NOTE: 'finance.invoices.create_credit_note',
  FINANCE_INVOICES_CANCEL: 'finance.invoices.cancel',
  FINANCE_PAYMENTS_REFUND: 'finance.payments.refund',
  FINANCE_STATEMENTS_SEND: 'finance.statements.send',

  // ===== WAVE RECONCILIATION (Cockpit CFO Phase 2/3) =====
  WAVE_VIEW: 'wave.view',
  WAVE_IMPORT: 'wave.import',
  WAVE_RECONCILE_AUTO: 'wave.reconcile.auto',
  WAVE_RECONCILE_MANUAL: 'wave.reconcile.manual',
  WAVE_FEES_VALIDATE: 'wave.fees.validate',
  WAVE_REPORT_GENERATE: 'wave.report.generate',
  WAVE_MONTH_CLOSE: 'wave.month.close',
  WAVE_MONTH_REOPEN: 'wave.month.reopen',
  WAVE_EXPORT: 'wave.export',
  WAVE_AUDIT_VIEW: 'wave.audit.view',
  FINANCE_DASHBOARD_VIEW: 'finance.dashboard.view',
  FINANCE_KPIS_VIEW: 'finance.kpis.view',
  FINANCE_PROFITABILITY_VIEW: 'finance.profitability.view',
  FINANCE_BUDGET_EDIT: 'finance.budget.edit',
  FINANCE_STRESS_TEST_RUN: 'finance.stress_test.run',
  FINANCE_EXPORT_OHADA: 'finance.export_ohada',
  FINANCE_PAYMENT_POLICIES_MANAGE: 'finance.payment_policies.manage',
  PAYMENTS_CREATE: 'payments.create',
  PAYMENTS_DELETE: 'payments.delete',

  // ===== PAYMENT REQUESTS (Wave workflow) =====
  PAYMENT_REQUESTS_VIEW: 'payment_requests.view',
  PAYMENT_REQUESTS_CREATE: 'payment_requests.create',
  PAYMENT_REQUESTS_VALIDATE: 'payment_requests.validate',
  PAYMENT_REQUESTS_DECLARE_PAID: 'payment_requests.declare_paid',
  PAYMENT_REQUESTS_CANCEL: 'payment_requests.cancel',

  // ===== CUSTOMER SUCCESS =====
  CUSTOMER_SUCCESS_VIEW: 'customer_success.view',
  CUSTOMER_SUCCESS_SCHEDULER_MANAGE: 'customer_success.scheduler.manage',

  // ===== REPORTS =====
  REPORTS_VIEW_DASHBOARD: 'reports.view_dashboard',
  REPORTS_VIEW_ANALYTICS: 'reports.view_analytics',
  REPORTS_EXPORT: 'reports.export',

  // ===== SETTINGS / SYSTEM =====
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_EDIT: 'settings.edit',
  SETTINGS_MANAGE_BACKUPS: 'settings.manage_backups',
  SETTINGS_BRANDING_EDIT: 'settings.branding.edit',
  SETTINGS_TEMPLATES_MANAGE: 'settings.templates.manage',
  STAFF_MANAGE: 'staff.manage',
  MCP_ACCESS: 'mcp.access',
  AUDIT_LOGS_VIEW: 'audit_logs.view',
  AUDIT_LOGS_EXPORT: 'audit_logs.export',

  // ===== TREASURY (Fév 2026) =====
  TREASURY_VIEW: 'treasury.view',
  TREASURY_REPORT_GENERATE: 'treasury.report.generate',
  TREASURY_REPORT_SEND: 'treasury.report.send',
  TREASURY_SETTINGS_VIEW: 'treasury.settings.view',
  TREASURY_SETTINGS_EDIT: 'treasury.settings.edit',

  // ===== ROLES =====
  ROLES_VIEW: 'roles.view',
  ROLES_CREATE: 'roles.create',
  ROLES_EDIT: 'roles.edit',
  ROLES_DELETE: 'roles.delete',

  // ===== INCIDENTS =====
  INCIDENTS_VIEW_OWN: 'incidents.view.own',
  INCIDENTS_VIEW_ALL: 'incidents.view.all',
  INCIDENTS_CREATE: 'incidents.create',
  INCIDENTS_RESOLVE: 'incidents.resolve',
  INCIDENTS_DELETE: 'incidents.delete',
  ALERTS_VIEW_ALL: 'alerts.view.all',
  ALERTS_ACKNOWLEDGE: 'alerts.acknowledge',
  ALERTS_CREATE_RULE: 'alerts.create_rule',
  REMINDERS_MANAGE: 'reminders.manage',

  // ===== FLEET (vehicles + drivers) =====
  FLEET_VIEW: 'fleet.view',
  FLEET_MANAGE: 'fleet.manage',
  DRIVERS_VIEW: 'drivers.view',
  DRIVERS_CREATE: 'drivers.create',
  DRIVERS_EDIT: 'drivers.edit',
  DRIVERS_VALIDATE: 'drivers.validate',
  DRIVERS_DEACTIVATE: 'drivers.deactivate',
  DRIVERS_ASSIGN: 'drivers.assign',

  // ===== TRACKING =====
  TRACKING_VIEW_OWN: 'tracking.view.own',
  TRACKING_VIEW_ALL: 'tracking.view.all',
  TRACKING_UPDATE_LOCATION: 'tracking.update_location',

  // ===== CLIENTS =====
  CLIENTS_VIEW_ALL: 'clients.view.all',
  CLIENTS_VIEW_OWN: 'clients.view.own',
  CLIENTS_CREATE: 'clients.create',
  CLIENTS_EDIT: 'clients.edit',
  CLIENTS_DELETE: 'clients.delete',
  CLIENTS_DEACTIVATE: 'clients.deactivate',
  CLIENTS_BULK_IMPORT: 'clients.bulk_import',
  CLIENTS_EXPORT: 'clients.export',
  CLIENTS_CONTACTS_MANAGE: 'clients.contacts.manage',
  CLIENTS_SUBACCOUNTS_MANAGE: 'clients.subaccounts.manage',
  CLIENTS_VIEW_PRICING: 'clients.view_pricing',
  CLIENTS_VIEW_CREDIT: 'clients.view_credit',
  TRANSPORT_PARTNERS_VIEW: 'transport_partners.view',
  TRANSPORT_PARTNERS_MANAGE: 'transport_partners.manage',

  // ===== DOCUMENTS & OCR =====
  DOCUMENTS_VIEW_ALL: 'documents.view.all',
  DOCUMENTS_VIEW_OWN: 'documents.view.own',
  DOCUMENTS_UPLOAD: 'documents.upload',
  DOCUMENTS_DELETE: 'documents.delete',
  DOCUMENTS_OCR_RUN: 'documents.ocr.run',
  DOCUMENTS_OCR_SETTINGS: 'documents.ocr.settings',
  INTERNAL_NOTES_VIEW: 'internal_notes.view',
  INTERNAL_NOTES_MANAGE: 'internal_notes.manage',

  // ===== BACKUPS =====
  BACKUPS_CREATE: 'backups.create',
  BACKUPS_RESTORE: 'backups.restore',
  BACKUPS_DOWNLOAD: 'backups.download',
  BACKUPS_DELETE: 'backups.delete',

  // ===== WEBHOOKS =====
  WEBHOOKS_VIEW: 'webhooks.view',
  WEBHOOKS_MANAGE: 'webhooks.manage',
  WEBHOOKS_REPLAY: 'webhooks.replay',
  WEBHOOKS_LOGS: 'webhooks.logs',

  // ===== TEAM =====
  TEAM_REASSIGN_OPS: 'team.reassign_ops',
  TEAM_REASSIGN_FIELD: 'team.reassign_field',
  TEAM_REASSIGN_COMMERCIAL: 'team.reassign_commercial',
  TEAM_VIEW_PERFORMANCE: 'team.view_performance',
});

// Permet d'importer `PERM_LIST` pour itérations (ex: rendre une matrice perm/role)
export const PERM_LIST = Object.values(PERMS);

export default PERMS;
